/**
 * Coarse visibility scoring module.
 * Estimates target clarity from bounds only, which keeps planning available even when
 * the richer mesh visibility context could not be built.
 */
import type { ViewpointBounds, ViewpointSceneContext, ViewpointVector3 } from '../generate-jig.types'
import type { ViewCandidate } from './view-decision-types'
import type { ViewpointPolicyOverrides } from './viewpoint-policy'
import { resolveViewpointPolicy } from './viewpoint-policy'
import {
  diagonal,
  directionToYawPitch,
  dot,
  projectAabbToViewport,
  rayIntersectsAabb,
  shortestAngleDeg,
  sub,
} from './viewpoint-math'

/**
 * Coarse visibility bucket used when comparing candidate viewpoints.
 */
export type VisibilityQuality = 'clear' | 'usable' | 'degraded'

/**
 * Fast, bounds-based visibility score for one candidate camera.
 */
export interface VisibilityMetrics {
  quality: VisibilityQuality
  visibleSampleRatio: number
  targetCoverage: number
  occluderOverlapRatio: number
  yawDeviationDeg: number
  pitchDeviationDeg: number
  isLifted: boolean
  aimPoint: ViewpointVector3
}

function qualityRank(quality: VisibilityQuality): number {
  if (quality === 'clear') {
    return 2
  }

  if (quality === 'usable') {
    return 1
  }

  return 0
}

function buildSamplePoints(targetBounds: ViewpointBounds, cameraPosition: ViewpointVector3): ViewpointVector3[] {
  // Sample the camera-facing sides of the target AABB so the planner can estimate
  // visibility without a mesh-backed scene.
  const direction = sub(cameraPosition, targetBounds.center)
  const sx = direction.x >= 0 ? 1 : -1
  const sy = direction.y >= 0 ? 1 : -1
  const sz = direction.z >= 0 ? 1 : -1
  const halfX = targetBounds.size.x / 2
  const halfY = targetBounds.size.y / 2
  const halfZ = targetBounds.size.z / 2
  const sampleMap: Record<string, ViewpointVector3> = {}

  function addPoint(point: ViewpointVector3): void {
    const key = point.x.toFixed(5) + ':' + point.y.toFixed(5) + ':' + point.z.toFixed(5)
    sampleMap[key] = point
  }

  ;[-1, 0, 1].forEach(function (ay) {
    ;[-1, 0, 1].forEach(function (az) {
      addPoint({
        x: targetBounds.center.x + (sx * halfX),
        y: targetBounds.center.y + (ay * halfY),
        z: targetBounds.center.z + (az * halfZ),
      })
    })
  })

  ;[-1, 0, 1].forEach(function (ax) {
    ;[-1, 0, 1].forEach(function (az) {
      addPoint({
        x: targetBounds.center.x + (ax * halfX),
        y: targetBounds.center.y + (sy * halfY),
        z: targetBounds.center.z + (az * halfZ),
      })
    })
  })

  ;[-1, 0, 1].forEach(function (ax) {
    ;[-1, 0, 1].forEach(function (ay) {
      addPoint({
        x: targetBounds.center.x + (ax * halfX),
        y: targetBounds.center.y + (ay * halfY),
        z: targetBounds.center.z + (sz * halfZ),
      })
    })
  })

  return Object.keys(sampleMap).map(function (key: string) {
    return sampleMap[key]
  })
}

function intersectsBounds(rayOrigin: ViewpointVector3, rayTarget: ViewpointVector3, bounds: ViewpointBounds): boolean {
  const ray = sub(rayTarget, rayOrigin)
  const rayLength = Math.sqrt(dot(ray, ray))
  if (rayLength < 1e-10) {
    return false
  }

  const rayDirection = {
    x: ray.x / rayLength,
    y: ray.y / rayLength,
    z: ray.z / rayLength,
  }

  return rayIntersectsAabb(
    rayOrigin,
    rayDirection,
    bounds.center,
    {
      x: bounds.size.x / 2,
      y: bounds.size.y / 2,
      z: bounds.size.z / 2,
    },
    rayLength
  )
}

function rectOverlapRatio(
  targetArea: number,
  targetProjection: ReturnType<typeof projectAabbToViewport>,
  occluderProjection: ReturnType<typeof projectAabbToViewport>
): number {
  if (!targetProjection || !occluderProjection || targetArea <= 1e-10) {
    return 0
  }

  const overlapWidth = Math.max(0, Math.min(targetProjection.maxX, occluderProjection.maxX) - Math.max(targetProjection.minX, occluderProjection.minX))
  const overlapHeight = Math.max(0, Math.min(targetProjection.maxY, occluderProjection.maxY) - Math.max(targetProjection.minY, occluderProjection.minY))

  return (overlapWidth * overlapHeight) / targetArea
}

/**
 * Scores visibility from axis-aligned bounds only.
 * This is the planner's baseline scorer and remains available even when no mesh-backed
 * visibility context was built for precise raycasts.
 */
export function scoreCandidate(candidate: ViewCandidate, options: {
  sceneContext: ViewpointSceneContext
  targetBounds: ViewpointBounds
  targetElementIds: Set<string>
  requestedDirection: ViewpointVector3
  nonOccludingIds?: Set<string>
  fovDeg?: number
  aspect?: number
  policy?: ViewpointPolicyOverrides
}): VisibilityMetrics {
  const fovDeg = typeof options.fovDeg === 'number' ? options.fovDeg : 60
  const aspect = typeof options.aspect === 'number' ? options.aspect : (16 / 9)
  const policy = resolveViewpointPolicy(options.policy)
  const cameraForward = sub(candidate.targetPosition, candidate.cameraPosition)
  const samplePoints = buildSamplePoints(options.targetBounds, candidate.cameraPosition)
  let visibleSamples = 0
  const visiblePoints: ViewpointVector3[] = []

  samplePoints.forEach(function (sample: ViewpointVector3) {
    let blocked = false

    options.sceneContext.elements.forEach(function (element) {
      if (blocked || options.targetElementIds.has(element.id) || (options.nonOccludingIds && options.nonOccludingIds.has(element.id))) {
        return
      }

      if (intersectsBounds(candidate.cameraPosition, sample, element.bounds)) {
        blocked = true
      }
    })

    if (!blocked) {
      visibleSamples++
      visiblePoints.push(sample)
    }
  })

  const targetProjection = projectAabbToViewport(options.targetBounds, candidate.cameraPosition, cameraForward, fovDeg, aspect)
  const targetCoverage = targetProjection ? targetProjection.area : 0
  const targetDepth = targetProjection ? targetProjection.maxDepth : diagonal(options.targetBounds.size)
  let overlapSum = 0

  options.sceneContext.elements.forEach(function (element) {
    if (options.targetElementIds.has(element.id) || (options.nonOccludingIds && options.nonOccludingIds.has(element.id))) {
      return
    }

    const occluderProjection = projectAabbToViewport(element.bounds, candidate.cameraPosition, cameraForward, fovDeg, aspect)
    if (!occluderProjection || occluderProjection.minDepth >= targetDepth) {
      return
    }

    overlapSum += rectOverlapRatio(targetCoverage, targetProjection, occluderProjection)
  })

  const requestedAngles = directionToYawPitch(options.requestedDirection)
  const actualAngles = directionToYawPitch(sub(candidate.cameraPosition, candidate.targetPosition))
  const visibleSampleRatio = samplePoints.length > 0 ? (visibleSamples / samplePoints.length) : 0
  const occluderOverlapRatio = Math.min(1, overlapSum)
  let quality: VisibilityQuality = 'degraded'

  if (
    visibleSampleRatio >= policy.visibility.clearVisibleSampleRatio &&
    occluderOverlapRatio <= policy.visibility.clearOccluderOverlapRatio &&
    targetCoverage >= policy.visibility.clearTargetCoverage
  ) {
    quality = 'clear'
  } else if (
    visibleSampleRatio >= policy.visibility.usableVisibleSampleRatio &&
    occluderOverlapRatio <= policy.visibility.usableOccluderOverlapRatio &&
    targetCoverage >= policy.visibility.usableTargetCoverage
  ) {
    quality = 'usable'
  }

  return {
    quality: quality,
    visibleSampleRatio: visibleSampleRatio,
    targetCoverage: targetCoverage,
    occluderOverlapRatio: occluderOverlapRatio,
    yawDeviationDeg: Math.abs(shortestAngleDeg(requestedAngles.yawDeg, actualAngles.yawDeg)),
    pitchDeviationDeg: Math.abs(actualAngles.pitchDeg - requestedAngles.pitchDeg),
    isLifted: candidate.liftOffset > 0,
    aimPoint: visiblePoints.length > 0 && visiblePoints.length < samplePoints.length
      ? {
        x: visiblePoints.reduce(function (sum, point) { return sum + point.x }, 0) / visiblePoints.length,
        y: visiblePoints.reduce(function (sum, point) { return sum + point.y }, 0) / visiblePoints.length,
        z: visiblePoints.reduce(function (sum, point) { return sum + point.z }, 0) / visiblePoints.length,
      }
      : options.targetBounds.center,
  }
}

/**
 * Orders two coarse scores from best to worst.
 * Lower return values mean `a` should win the planner comparison.
 */
export function compareVisibilityMetrics(a: VisibilityMetrics, b: VisibilityMetrics, aDistance: number, bDistance: number): number {
  if (qualityRank(a.quality) !== qualityRank(b.quality)) {
    return qualityRank(b.quality) - qualityRank(a.quality)
  }

  if (a.targetCoverage !== b.targetCoverage) {
    return b.targetCoverage - a.targetCoverage
  }

  if (a.visibleSampleRatio !== b.visibleSampleRatio) {
    return b.visibleSampleRatio - a.visibleSampleRatio
  }

  if (a.occluderOverlapRatio !== b.occluderOverlapRatio) {
    return a.occluderOverlapRatio - b.occluderOverlapRatio
  }

  if (a.yawDeviationDeg !== b.yawDeviationDeg) {
    return a.yawDeviationDeg - b.yawDeviationDeg
  }

  if (a.pitchDeviationDeg !== b.pitchDeviationDeg) {
    return a.pitchDeviationDeg - b.pitchDeviationDeg
  }

  if (a.isLifted !== b.isLifted) {
    return Number(a.isLifted) - Number(b.isLifted)
  }

  return aDistance - bDistance
}
